/* Turning a file somebody picked into something this app can store and draw.
 *
 * Read as a data URL rather than an object URL. The desktop shell serves the
 * app under a content policy of `img-src 'self' data:` — no `blob:` — so an
 * object URL is refused before the image ever decodes, and every format looks
 * equally broken. Widening the policy to admit blobs would fix the symptom by
 * loosening the app; this needs nothing loosened.
 *
 * Squared and scaled down on the way in, because what goes in is whatever the
 * camera or the website produced and what comes out sits in IndexedDB next to
 * the book. A four-megabyte photograph behind a thirty-four-pixel tile is four
 * megabytes of the storage the theses live in. */

export interface SquareOptions {
  /** Side of the result, in pixels. */
  px: number;
  /** PNG keeps transparency, which a logo usually has and a photograph never
      needs. JPEG at 0.85 is a third of the size and fills transparency black. */
  mime: 'image/png' | 'image/jpeg';
}

/** Reads a file to a data URL without touching it. Used on its own where the
    bytes are already the right shape. */
export function readDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('unreadable file'));
    reader.readAsDataURL(file);
  });
}

/**
 * A square data URL of `px` a side.
 *
 * `fit` decides what happens to an image that is not already square. 'cover'
 * takes the middle and crops the rest — right for a face, where squashing is
 * worse than cropping. 'contain' fits the whole thing in and leaves the rest
 * transparent — right for a logo, where cropping loses the half of a wordmark
 * that says which company it is.
 */
export async function squareDataUrl(
  file: File,
  { px, mime }: SquareOptions,
  fit: 'cover' | 'contain' = 'cover',
): Promise<string> {
  const dataUrl = await readDataUrl(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('not an image'));
      img.src = dataUrl;
    });

    const canvas = document.createElement('canvas');
    canvas.width = px;
    canvas.height = px;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no canvas');

    if (fit === 'cover') {
      const side = Math.min(image.width, image.height);
      ctx.drawImage(
        image,
        (image.width - side) / 2, (image.height - side) / 2, side, side,
        0, 0, px, px,
      );
    } else {
      const scale = Math.min(px / image.width, px / image.height);
      const w = image.width * scale;
      const h = image.height * scale;
      ctx.drawImage(image, (px - w) / 2, (px - h) / 2, w, h);
    }

    return mime === 'image/jpeg' ? canvas.toDataURL('image/jpeg', 0.85) : canvas.toDataURL('image/png');
  } catch {
    // A file the browser cannot decode — a HEIC straight off an iPhone, say.
    throw new Error('not an image this device can decode');
  }
}
